const { mapValues, invert, invoke, sum, sumBy, uniq, keyBy, } = require('lodash');

const remotePostalCodes = {};

const metricSum = (metricName, values) => sumBy(values, _ => _?.[metricName]?.value) ?? 0;
const metricAvg = (metricName, values) => metricSum(metricName, values) / values.length;

const addFormat = _ => mapValues(_, _ => ({
  ..._,
  format: ({
    quantity: _ => _?.avg ? '0,0.0' : '0,0',
    amount: _ => _?.avg ? '0,0.0' : '0,0',
    rate: _ => '0,0.0%',
  })[_.type],
}));

const saleMetrics = addFormat(mapValues({
  sessionsCount: {
    label: 'セッション',
    type: 'quantity',
    total: metricSum.bind(null, 'sessionsCount'),
    avg: metricAvg.bind(null, 'sessionsCount'),
    reportKey: 'trafficByAsin.sessions',
  },
  unitSessionsRate: {
    label: 'ユニットセッション率',
    type: 'rate',
    total: (values) => (metrics.salesCount.total(values) + metrics.adjustingSalesCount.total(values)) / metrics.sessionsCount.total(values),
    avg: (values) => (metrics.salesCount.avg(values) + metrics.adjustingSalesCount.avg(values)) / metrics.sessionsCount.avg(values),
  },
  salesCount: {
    label: '販売数',
    type: 'quantity',
    total: metricSum.bind(null, 'salesCount'),
    avg: metricAvg.bind(null, 'salesCount'),
    reportKey: 'salesByAsin.unitsOrdered',
  },
  adjustingSalesCount: {
    label: '販売数調整',
    type: 'quantity',
    total: metricSum.bind(null, 'adjustingSalesCount'),
    avg: metricAvg.bind(null, 'adjustingSalesCount'),
  },
  salesAmount: {
    label: '売上',
    type: 'amount',
    total: metricSum.bind(null, 'salesAmount'),
    avg: metricAvg.bind(null, 'salesAmount'),
    reportKey: 'salesByAsin.orderedProductSales.amount',
  },
  adjustingSalesAmount: {
    label: '売上調整',
    type: 'amount',
    total: metricSum.bind(null, 'adjustingSalesAmount'),
    avg: metricAvg.bind(null, 'adjustingSalesAmount'),
  },
}, _ => ({ ..._, category: 'sales', })));

const adMetrics = addFormat(mapValues({
  spImpressionsCount: {
    label: 'SPインプ',
    type: 'quantity',
    total: metricSum.bind(null, 'spImpressionsCount'),
    avg: metricAvg.bind(null, 'spImpressionsCount'),
    reportKey: 'impressions',
  },
  spClicksCount: {
    label: 'SPクリック',
    type: 'quantity',
    total: metricSum.bind(null, 'spClicksCount'),
    avg: metricAvg.bind(null, 'spClicksCount'),
    reportKey: 'clicks',
  },
  spPurchasesCount: {
    label: 'SP販売数',
    type: 'quantity',
    total: metricSum.bind(null, 'spPurchasesCount'),
    avg: metricAvg.bind(null, 'spPurchasesCount'),
    reportKey: 'purchases1d',
    sbReportKey: 'purchases',
  },
  spCvr: {
    label: 'SP CVR',
    type: 'rate',
    total: (vs) => metrics.spPurchasesCount.total(vs) / metrics.spClicksCount.total(vs),
    avg: (vs) => metrics.spPurchasesCount.avg(vs) / metrics.spClicksCount.avg(vs),
  },
  spAdAmount: {
    label: 'SP広告',
    type: 'amount',
    total: metricSum.bind(null, 'spAdAmount'),
    avg: metricAvg.bind(null, 'spAdAmount'),
    reportKey: 'cost',
    withTax: 1.1,
  },
  acos: {
    label: 'ACOS',
    type: 'rate',
    total: (vs) => metrics.spAdAmount.total(vs) / metrics.spSalesAmount.total(vs),
    avg: (vs) => metrics.spAdAmount.avg(vs) / metrics.spSalesAmount.avg(vs),
  },
  spSalesAmount: {
    label: 'SP売上',
    type: 'amount',
    total: metricSum.bind(null, 'spSalesAmount'),
    avg: metricAvg.bind(null, 'spSalesAmount'),
    reportKey: 'sales1d',
    sbReportKey: 'sales',
  },
}, _ => ({ ..._, category: 'ad' })));

const metrics = {
  ...saleMetrics,
  ...adMetrics,
  ...addFormat({
    acots: {
      category: 'ad',
      label: 'TACOS',
      type: 'rate',
      total: (vs) => metrics.spAdAmount.total(vs) / (metrics.salesAmount.total(vs) + metrics.adjustingSalesAmount.total(vs)),
      avg: (vs) => metrics.spAdAmount.avg(vs) / metrics.spSalesAmount.avg(vs),
    },
  }),
};
const profitMetrics = addFormat({
  purchasingCost: {
    type: 'amount',
    compute: _ => sumBy(_, 'costs.purchasingCost'),
  },
  salesFee: {
    type: 'amount',
    compute: _ => sumBy(_, 'salesFee'),
  },
  shippingFee: {
    type: 'amount',
    compute: _ => sumBy(_, 'costs.shippingFee'),
  },
  reward: {
    type: 'amount',
    compute: _ => sumBy(_, 'reward'),
  },
  profit: {
    type: 'amount',
    compute: _ => sumBy(_, 'profit'),
  },
  profitRate: {
    type: 'rate',
    compute: (p, k) => profitMetrics.profit.compute(p) / (metrics.salesAmount.total(k) + metrics.adjustingSalesAmount.total(k)),
  },
});

const prefectures = {
  1: '北海道', 2: '青森県', 3: '岩手県', 4: '宮城県', 5: '秋田県',
  6: '山形県', 7: '福島県', 8: '茨城県', 9: '栃木県', 10: '群馬県',
  11: '埼玉県', 12: '千葉県', 13: '東京都', 14: '神奈川県', 15: '新潟県',
  16: '富山県', 17: '石川県', 18: '福井県', 19: '山梨県', 20: '長野県',
  21: '岐阜県', 22: '静岡県', 23: '愛知県', 24: '三重県', 25: '滋賀県',
  26: '京都府', 27: '大阪府', 28: '兵庫県', 29: '奈良県', 30: '和歌山県',
  31: '鳥取県', 32: '島根県', 33: '岡山県', 34: '広島県', 35: '山口県',
  36: '徳島県', 37: '香川県', 38: '愛媛県', 39: '高知県', 40: '福岡県',
  41: '佐賀県', 42: '長崎県', 43: '熊本県', 44: '大分県', 45: '宮崎県',
  46: '鹿児島県', 47: '沖縄県',
};

const taxRates = {
  tax10: { value: 0.1, label: '10%' },
  tax8: { value: 0.08, label: '8%' },
  exempt: { value: 0, label: '対象外' },
};

const generalTaxRate = taxRates.tax10.value;

exports.saleMetrics = saleMetrics;
exports.adMetrics = adMetrics;
exports.metrics = metrics;
exports.prefectures = prefectures;
exports.remotePostalCodes = remotePostalCodes;
exports.profitMetrics = profitMetrics;
exports.taxRates = taxRates;
exports.generalTaxRate = generalTaxRate;
